import { Component, Input } from '@angular/core';


export interface IAvatarSettings {
  fullName?: string;
  isShowLabelNameLeft?: boolean;
  isShowFirstName?: boolean;
  isShowLabelNameRight?: boolean;
  minInitialsLength?: number;
  imageUrl?: string;
  size?: number;
  background?: string;
}

@Component({
  selector: 'sgt-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
})

export class AvatarComponent {
  initials: string | null = null;
  @Input() settings: IAvatarSettings = {};

  private readonly COLORS = [
    '#9d9d9d',
    '#525252',
    '#fb5c5c',
    '#b11313',
    '#f14394',
    '#c440cd',
    '#920d9b',
    '#7a39f9',
    '#4562ef',
    '#2d84ad',
    '#35bfcb',
    '#2ac785',
    '#158153',
    '#63c343',
    '#a5cd06',
    '#d3cc03',
    '#f9dd15',
    '#f97e15',
    '#c95c00'
  ];

  private readonly PREPOSITIONS = ['de', 'da', 'do', 'das', 'dos'];
  private readonly MAX_INITIALS_LENGTH = 2;
  readonly DEFAULT_SIZE = 25;
  readonly DEFAULT_INITIALS = '';
  readonly DEFAULT_BACKGROUND = '#9d9d9d';

  get avatarInitials(): string {
    if (this.settings?.fullName) {
      return this.createInitials();
    }

    return this.DEFAULT_INITIALS;
  }

  get backgroundColor(): string {
    if (this.settings?.background) {
      return this.settings.background;
    }

    if (this.settings?.imageUrl) {
      return 'transparent';
    }

    const name = this.settings?.fullName || this.DEFAULT_INITIALS;
    return this.generateColorFromName(name);
  }

  get displayName(): string {
    if (!this.settings?.fullName) {
      return '-';
    }

    if (this.settings?.isShowFirstName) {
      const firstName = this.settings.fullName.trim().split(/\s+/)[0];
      return firstName || '-';
    }

    return this.settings.fullName;
  }

  /**
   * Cria iniciais a partir de um nome composto
   *
   * @returns Iniciais do nome (baseado em minInitialsLength e maxInitials)
   */
  private createInitials(): string {
    const fullName = this.settings?.fullName;
    if (!fullName || fullName.trim().length === 0) {
      return this.DEFAULT_INITIALS;
    }

    const names = this.parseAndFilterNames(fullName);
    if (names.length === 0) {
      return this.DEFAULT_INITIALS;
    }

    const minLength = this.getMinInitialsLength();

    return names.length === 1
      ? this.createSingleNameInitials(names[0], minLength)
      : this.createMultipleNamesInitials(names, minLength);
  }

  /**
   * Analisa e filtra o nome completo, removendo preposições e partes vazias
   *
   * @param fullName Nome completo a ser processado
   * @returns Array de nomes válidos
   */
  private parseAndFilterNames(fullName: string): string[] {
    return fullName
      .trim()
      .split(/\s+/)
      .filter((name: string) => name.length > 0)
      .filter((name: string) => !this.hasPreposition(name));
  }

  /**
   * Obtém o comprimento mínimo das iniciais baseado nas configurações
   * Garante que não exceda o máximo permitido (MAX_INITIALS_LENGTH)
   *
   * @returns Comprimento mínimo das iniciais (limitado ao máximo)
   */
  private getMinInitialsLength(): number {
    const configuredLength = this.settings?.minInitialsLength || this.MAX_INITIALS_LENGTH;
    return Math.min(configuredLength, this.MAX_INITIALS_LENGTH);
  }

  /**
   * Cria iniciais para um nome único
   *
   * @param singleName Nome único
   * @param minLength Comprimento mínimo desejado (já limitado pelo máximo)
   * @returns Iniciais do nome único
   */
  private createSingleNameInitials(singleName: string, minLength: number): string {
    if (singleName.length === 1) {
      return singleName.toUpperCase();
    }

    const lengthToTake = Math.min(minLength, singleName.length, this.MAX_INITIALS_LENGTH);
    return singleName.substring(0, lengthToTake).toUpperCase();
  }

  /**
   * Cria iniciais para múltiplos nomes
   *
   * @param names Array de nomes
   * @param minLength Comprimento mínimo desejado
   * @returns Iniciais dos múltiplos nomes
   */
  private createMultipleNamesInitials(names: string[], minLength: number): string {
    let initials = this.getFirstLettersFromNames(names);

    if (initials.length < minLength) {
      initials = this.expandInitialsWithFirstName(initials, names[0], minLength);
    }

    return initials;
  }

  /**
   * Extrai a primeira letra de cada nome
   *
   * @param names Array de nomes
   * @returns String com as primeiras letras
   */
  private getFirstLettersFromNames(names: string[]): string {
    return names
      .slice(0, this.MAX_INITIALS_LENGTH)
      .map((name: string) => name.charAt(0).toUpperCase())
      .join('');
  }

  /**
   * Expande as iniciais com caracteres adicionais do primeiro nome
   *
   * @param currentInitials Iniciais atuais
   * @param firstName Primeiro nome
   * @param minLength Comprimento mínimo desejado
   * @returns Iniciais expandidas (limitadas ao máximo permitido)
   */
  private expandInitialsWithFirstName(currentInitials: string, firstName: string, minLength: number): string {
    const firstNameCharsNeeded = minLength - (currentInitials.length - 1);
    const firstNameExtra = firstName.substring(1, firstNameCharsNeeded);
    const expandedInitials = firstName.charAt(0).toUpperCase() + firstNameExtra.toUpperCase() + currentInitials.substring(1);

    // Garante que não exceda o máximo permitido
    return expandedInitials.substring(0, this.MAX_INITIALS_LENGTH);
  }

  /**
   * Verifica se uma palavra é uma preposição ou artigo
   *
   * @param word Palavra a ser verificada
   * @returns true se for uma preposição
   */
  private hasPreposition(word: string): boolean {
    return this.PREPOSITIONS.includes(word.toLowerCase());
  }

  /**
   * Gera uma cor consistente baseada no nome
   *
   * @param name Nome para gerar a cor
   * @returns Cor em formato hexadecimal
   */
  private generateColorFromName(name: string): string {
    if (!name || name.trim().length === 0) {
      return this.COLORS[0];
    }

    // Para cor consistente baseada no nome:
    const hash = Math.abs(this.hashCode(name.trim().toLowerCase()));
    const colorIndex = hash % this.COLORS.length;
    return this.COLORS[colorIndex];
  }

  /**
   * Cria um hash code simples para uma string
   *
   * @param str String para gerar hash
   * @returns Número hash
   */
  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash += str.charCodeAt(i);
    }
    return hash;
  }

  /**
   * Calcula o tamanho da fonte baseado no tamanho do avatar
   *
   * @returns Tamanho da fonte em pixels
   */
  get fontSize(): string {
    const height = this.settings?.size || this.DEFAULT_SIZE;
    return `${Math.floor(height / 3)}px`;
  }
}
